// These are the possible keys that instructions can be associated with.
// Use getAssociateInstructionsKeys with a FrameSpec to get the correct keys.
// 
// Any given framespec will have one of the following keys:
//    "rolled-canvas"
//    "board-or-canvas-panel"
//    "stretched-canvas"
//    "curated-layout-jersey"
//    "custom-float-jersey"
//    "document"
//    "multi-artwork"
//    "newspaper-and-magazine"
//    "object"
//    "curated-layout-object"
//    "original-art"
//    "physical-photo"
//    "print-or-poster"
//    "textile"
//    "digital-photo"
// 
// Some criteria for instructions can be applied to multiple art types. In that case additional keys
// may be included. For example "document" and "clear-float" may both be included for a framespec.
// 
// Possible additional keys:
//    "clear-float"


export const getAssociateInstructionsKeys = (frameSpec) => {
  const keys = [];
  const productCategory = frameSpec.productCategory();

  // Some of these keys may be applied:
  if (frameSpec.isClearFloated()) {
    keys.push("clear-float");
  }

  // One of these keys must be included:
  if (productCategory === "multi_artwork") {
    keys.push("multi-artwork");
  } else if (productCategory === "photography") {
    keys.push("physical-photo");
  } else if (productCategory === "prints_and_posters") {
    keys.push("print-or-poster");
  } else if (productCategory === "diplomas_and_certificates") {
    keys.push("document");
  } else if (productCategory === "original_art") {
    keys.push("original-art");
  } else if (productCategory === "newspaper_and_magazine") {
    keys.push("newspaper-and-magazine");
  } else if (productCategory === "object" && frameSpec.isCuratedLayoutGolfBallProduct()) {
    keys.push("curated-layout-object");
  } else if (productCategory === "object") {
    keys.push("object");
  } else if (productCategory === "jersey") {
    if (frameSpec.isCuratedLayoutJerseyProduct()) {
      keys.push("curated-layout-jersey");
    } else {
      keys.push("custom-float-jersey");
    }
  } else if (productCategory === "canvas" || productCategory === "canvas_paper") {
    const productCategorySubtype = frameSpec.getArtwork().productCategorySubtype;
    if (productCategorySubtype === "rolled-canvas") {
      keys.push("rolled-canvas");
    } else if (productCategorySubtype === "board-or-canvas-panel") {
      keys.push("board-or-canvas-panel");
    } else {
      keys.push("stretched-canvas");
    }
  } else if (productCategory === "textile") {
    keys.push("textile");
  } else if (productCategory === "digital_photo") {
    keys.push("digital-photo");
  }

  return keys;
};

export const associateInstructions = [
  {
    name: "Double Sided Art",
    category: "handling",
    requiredFor: ["document", "newspaper-and-magazine", "textile"],
    optionalFor: ["multi-artwork", "physical-photo", "print-or-poster", "original-art", "object", "rolled-canvas", "board-or-canvas-panel", "stretched-canvas"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Is the artwork double-sided?",
    },
    followUpQuestions: [
      {
        type: "text-area",
        required: true,
        text: "(Required) Please describe which side should be visible in the frame",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "double_sided_art"
  },
  {
    name: "Encapsulation",
    category: "handling",
    requiredFor: ["original-art"],
    optionalFor: ["document", "multi-artwork", "newspaper-and-magazine", "physical-photo", "print-or-poster"],
    disabledForMatStyles: ['no-mat', 'clear-float', 'float'],
    primaryQuestion: {
      type: "boolean",
      text: "Is the artwork transferrable?",
    },
    followUpQuestions: [
      {
        type: "tip",
        required: false,
        text: "Encapsulation involves placing the artwork between two sheets of clear acrylic to gently hold the piece in place. This method creates a sealed, but non-adhesive enclosure.",
        imageUrl: "https://cdn.shopify.com/s/files/1/0769/3602/0283/files/Mat-Reveal-Instruction-Tip-Image.png?v=1757615182"
      },
      {
        type: "checkbox",
        required: true,
        text: "Please confirm the artwork is very fragile or a transferable medium (rubs or sheds easily, such as charcoal)",
        values: ["Confirm Encapsulation"],
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "requires_encapsulation"
  },
  {
    name: "Existing Mat",
    category: "handling",
    requiredFor: [],
    optionalFor: ["rolled-canvas", "board-or-canvas-panel", "stretched-canvas", "curated-layout-jersey", "custom-float-jersey", "document", "multi-artwork", "newspaper-and-magazine", "object", "original-art", "physical-photo", "print-or-poster", "textile"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Existing Mat",
    },
    followUpQuestions: [
      {
        type: "radio",
        required: true,
        text: "Please specify treatment",
        values: [
          "Keep & frame as-is",
          "Keep & use as accent mat",
          "Remove & discard",
          "Remove & return to customer"
        ],
        summarySchema: {
          summaryPrefix: ""
        }
      },
      {
        type: "text-area",
        required: false,
        text: "Additional Notes (Optional)",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "has_existing_mat"
  },
  {
    name: "Digital Files for Printing",
    category: "handling",
    requiredFor: [],
    optionalFor: ["multi-artwork"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Digital Files for Printing",
    },
    followUpQuestions: [
      {
        type: "upload-files",
        required: false,
        text: "Please upload image(s) to print and indicate the customer-desired print size (e.g. 5x7) for each file.",
        summarySchema: {
          summaryPrefix: ""
        }
      },
      {
        type: "text-area",
        required: true,
        text: "(Required) Please specify desired print size for each image upload",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "multi_conveyance"
  },
  {
    name: "Final Frame Size Requirement",
    category: "handling",
    requiredFor: [],
    optionalFor: ["rolled-canvas", "board-or-canvas-panel", "stretched-canvas", "custom-float-jersey", "document", "multi-artwork", "newspaper-and-magazine", "object", "original-art", "physical-photo", "print-or-poster", "textile", "digital-photo"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Has Final Frame Size Requirements",
    },
    followUpQuestions: [
      {
        type: "dimensions",
        required: true,
        text: "Please specify",
      },
      {
        type: "text-area",
        required: false,
        text: "Additional Notes (Optional)",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "FFS_requirement"
  },
  {
    name: "Matching Set",
    category: "handling",
    requiredFor: [],
    optionalFor: ["rolled-canvas", "board-or-canvas-panel", "stretched-canvas", "custom-float-jersey", "document", "multi-artwork", "newspaper-and-magazine", "object", "original-art", "physical-photo", "print-or-poster", "textile", "digital-photo"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Part of a Matching Set",
    },
    followUpQuestions: [
      {
        type: "radio-with-text-areas",
        required: true,
        text: "Which frames are supposed to match?",
        values: [
          {
            label: "Match to other items in this order",
            followUpQuestions: [
              {
                type: "text-area",
                required: true,
                text: "(Required) Additional Notes",
                summarySchema: {
                  summaryPrefix: ""
                }
              },
            ]
          },
          {
            label: "Match to items in a previous order",
            note: "*If you don’t know the matching work order number, please go back and use the Final Frame Size (“FFS”) instruction instead.",
            followUpQuestions: [
              {
                type: "text-area",
                required: true,
                text: "(Required) Previous Work Order Number or Line Item Number to Match",
                summarySchema: {
                  summaryPrefix: ""
                }
              },
              {
                type: "text-area",
                required: false,
                text: "Additional Notes (Optional)",
                summarySchema: {
                  summaryPrefix: ""
                }
              },
            ]
          }
        ],
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "matching_set_instruction"
  },
  {
    name: "Mat Reveal / Spacing",
    category: "mounting",
    requiredFor: ["rolled-canvas", "board-or-canvas-panel", "stretched-canvas", "document", "newspaper-and-magazine", "object", "original-art", "physical-photo", "print-or-poster", "textile"],
    optionalFor: [],
    disabledForMatStyles: ['no-mat', 'clear-float', 'float'],
    primaryQuestion: {
      type: "boolean",
      text: "Does the customer have any preference on mat reveal / spacing?",
    },
    followUpQuestions: [
      {
        type: "checkbox",
        required: false,
        text: "",
        values: [
          "Image Reflects Customer Desired Layout"
        ],
        summarySchema: {
          summaryPrefix: ""
        }
      },
      {
        type: "radio",
        required: true,
        text: "Please specify treatment",
        values: [
          "Recommended/Standard: Show decorative borders, signatures, and/or print edition numbers",
          "Exception: Hide decorative borders, signatures, and/or print edition numbers"
        ],
        summarySchema: {
          summaryPrefix: ""
        }
      },
      {
        type: "text-area",
        required: false,
        text: "Additional Notes (Optional)",
        summarySchema: {
          summaryPrefix: ""
        }
      },
      {
        type: "tip",
        required: false,
        text: "Tip: If artwork extends beyond the mat, increase mat width to cover. Alternatively, please go back to the “Permission to Trim” instruction and provide permission to trim the artwork.",
        imageUrl: "https://cdn.shopify.com/s/files/1/0769/3602/0283/files/Mat-Reveal-Instruction-Tip-Image.png?v=1757615182"
      }
    ],
    tag: "has_reveal_instruction"
  },
  {
    name: "Multi Layout",
    category: "mounting",
    requiredFor: ["multi-artwork"],
    optionalFor: [],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Does the photo reflect the customer desired layout?",
      showPreview: true,
      showFollowUpQuestionsIfValue: "No"
    },
    followUpQuestions: [
      {
        type: "text-area",
        required: true,
        text: "Notes (Required)",
        note: "Please describe the customer-desired layout and preferences for spacing between each item (Or if possible, go back and update the photo)",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "photo_does_not_match_layout"
  },
  {
    name: "Custom Jersey Layout",
    category: "mounting",
    requiredFor: ["custom-float-jersey"],
    optionalFor: [],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Does the photo reflect the customer desired layout?",
      showPreview: true,
      showFollowUpQuestionsIfValue: "No"
    },
    followUpQuestions: [
      {
        type: "text-area",
        required: true,
        text: "Notes (Required)",
        note: "Please describe the customer-desired layout and any preferences related to placement / folding (Or if possible, go back and update the photo)",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "photo_does_not_match_layout"
  },
  {
    name: "Textile Tag",
    category: "mounting",
    requiredFor: ["curated-layout-jersey", "custom-float-jersey", "textile"],
    optionalFor: ["multi-artwork"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Are there tags, labels, or stickers attached to the artwork or textile?",
    },
    followUpQuestions: [
      {
        type: "radio",
        required: true,
        text: "Please specify the customer-desired treatment",
        values: [
          "Our Recommendation (Standard): Hide or tuck them in", 
          "Exception: Permission to remove",
          "Exception: Permission to show"
        ],
        summarySchema: {
          summaryPrefix: ""
        }
      },
      {
        type: "text-area",
        required: false,
        text: "Additional Notes (Optional)",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "textile_tag"
  },
  {
    name: "Permission to Stretch or Restretch Canvas",
    category: "mounting",
    requiredFor: [],
    optionalFor: ["rolled-canvas", "stretched-canvas"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Permission to Stretch or Restretch Canvas",
    },
    followUpQuestions: [
      {
        type: "text-area",
        required: false,
        text: "Additional Notes (Optional)",
        summarySchema: {
          summaryPrefix: ""
        }
      },
      {
        type: "tip",
        required: false,
        text: "Note: We always default to keeping art in its original state unless absolutely necessary or requested.",
      },
    ],
    tag: "canvas_stretching"
  },
  {
    name: "Clear Float Corner",
    category: "mounting",
    requiredFor: ["clear-float"],
    optionalFor: [],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Would the customer like to use clear float corners?",
      tooltip: "Clear float corners are recommended for double-sided art pieces (such as a postcard) in a clear float frame.",
      tooltipImageUrl: "https://cdn.shopify.com/s/files/1/0769/3602/0283/files/Mat-Reveal-Instruction-Tip-Image.png?v=1757615182",
    },
    followUpQuestions: [
      {
        type: "checkbox",
        required: true,
        text: "Please confirm the customers clear float mounting preference",
        values: [
          "Black"
        ],
        summarySchema: {
          summaryPrefix: ""
        }
      },
    ],
    tag: "photo_corners_required"
  },
  {
    name: "Permission to Trim",
    category: "mounting",
    requiredFor: ["newspaper-and-magazine"],
    optionalFor: ["document", "multi-artwork", "object", "original-art", "physical-photo", "print-or-poster"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Do we have permission to trim artwork?",
    },
    followUpQuestions: [
      {
        type: "tip",
        required: false,
        text: "Tip: If artwork extends beyond the mat, please provide Permission to Trim the artwork. Alternatively, go back to extend the mat width to cover the art.",
        imageUrl: "https://cdn.shopify.com/s/files/1/0769/3602/0283/files/Mat-Reveal-Instruction-Tip-Image.png?v=1757615182"
      },
      {
        type: "tip",
        required: false,
        text: "Note: We always default to keeping art in its original state unless absolutely necessary or requested.",
      },
      {
        type: "radio",
        required: true,
        text: "Please specify treatment",
        values: [
          "Trim and return trimmings to customer",
          "Trim and discard trimmings"
        ],
        summarySchema: {
          summaryPrefix: ""
        }
      },
      {
        type: "text-area",
        required: false,
        text: "Additional Notes (Optional)",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "permission_to_trim"
  },
  {
    name: "Hardware/Backing",
    category: "finishing",
    requiredFor: [],
    optionalFor: ["rolled-canvas", "board-or-canvas-panel", "stretched-canvas", "curated-layout-jersey", "custom-float-jersey", "document", "multi-artwork", "newspaper-and-magazine", "object", "curated-layout-object", "original-art", "physical-photo", "print-or-poster", "textile", "digital-photo"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Hardware / Backing Instructions",
    },
    followUpQuestions: [
      {
        type: "checkbox",
        required: false,
        text: "Include Paper Pocket on the back of the frame?",
        values: [
          "Yes"
        ],
        summarySchema: {
          summaryPrefix: "Paper pocket?:"
        }
      },
      {
        type: "text-area",
        required: false,
        text: "Additional Notes (Optional)",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "hardware_instruction"
  },
  {
    name: "Custom Handling Instructions",
    category: "handling",
    requiredFor: [],
    optionalFor: ["rolled-canvas", "board-or-canvas-panel", "stretched-canvas", "curated-layout-jersey", "custom-float-jersey", "document", "multi-artwork", "newspaper-and-magazine", "object", "curated-layout-object", "original-art", "physical-photo", "print-or-poster", "textile", "digital-photo"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Custom Handling Instructions",
    },
    followUpQuestions: [
      {
        type: "text-area",
        required: true,
        text: "(Required) Please describe the nature of the piece and instructions for special handling",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "custom_handling_instruction"
  },
  {
    name: "Custom Mounting / Matting Instruction",
    category: "mounting",
    requiredFor: [],
    optionalFor: ["rolled-canvas", "board-or-canvas-panel", "stretched-canvas", "custom-float-jersey", "document", "multi-artwork", "newspaper-and-magazine", "object", "curated-layout-object", "original-art", "physical-photo", "print-or-poster", "textile", "digital-photo"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Custom Mounting Instruction",
    },
    followUpQuestions: [
      {
        type: "text-area",
        required: true,
        text: "(Required) Note any desired matting or mounting instructions. Note some float options will not be eligible to ensure art safety.",
        note: "Note some float options will not be eligible to ensure art safety.",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "custom_mounting_instruction"
  },
  {
    name: "Custom Finishing Instruction",
    category: "finishing",
    requiredFor: [],
    optionalFor: ["rolled-canvas", "board-or-canvas-panel", "stretched-canvas", "curated-layout-jersey", "custom-float-jersey", "document", "multi-artwork", "newspaper-and-magazine", "object", "curated-layout-object", "original-art", "physical-photo", "print-or-poster", "textile", "digital-photo"],
    disabledForMatStyles: [],
    primaryQuestion: {
      type: "boolean",
      text: "Custom Finishing Instructions",
    },
    followUpQuestions: [
      {
        type: "text-area",
        required: true,
        text: "(Required) Please specify finishing instructions, e.g. spacer instructions, etc.",
        summarySchema: {
          summaryPrefix: ""
        }
      }
    ],
    tag: "custom_finishing_instruction"
  },
];

export default associateInstructions;
